import { useQuery } from "@tanstack/react-query";
import type {
	PassIdOption,
	ProductMillSetup,
	StandOption,
} from "../models/ProductMillSetup";
import {
	getActivePassIds,
	getActiveStands,
	getProductMillSetup,
} from "../repositories/ProductMillSetupRepository";

export const PRODUCT_MILL_SETUP_QUERY_KEYS = {
	all: ["productMillSetup"] as const,
	byProductSize: (productSizeId: number | null) =>
		["productMillSetup", productSizeId] as const,
	stands: ["productMillSetup", "stands"] as const,
	passIds: ["productMillSetup", "passIds"] as const,
};

export const useProductMillSetup = (productSizeId: number | null) => {
	const query = useQuery<ProductMillSetup[], Error>({
		queryKey: PRODUCT_MILL_SETUP_QUERY_KEYS.byProductSize(productSizeId),
		queryFn: () => getProductMillSetup(productSizeId!),
		enabled: !!productSizeId,
		// Switching tabs remounts the table: without this every switch refetches.
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
	});

	return {
		millSetupRows: query.data ?? [],
		isLoading: query.isLoading,
		error: query.error,
		refetch: query.refetch,
	};
};

export const useActiveStands = () => {
	return useQuery<StandOption[], Error>({
		queryKey: PRODUCT_MILL_SETUP_QUERY_KEYS.stands,
		queryFn: getActiveStands,
		staleTime: 5 * 60 * 1000,
	});
};

export const useActivePassIds = () => {
	return useQuery<PassIdOption[], Error>({
		queryKey: PRODUCT_MILL_SETUP_QUERY_KEYS.passIds,
		queryFn: getActivePassIds,
		// Always fresh: the stands a pass is usable on are edited in Pass IDs, another screen.
	});
};
